/**
 * Leaf React hooks for the browser preferences and shortcuts an app needs at
 * the edges: answer a media query, keep a value across reloads, bind a keyboard
 * shortcut. These are the boundary between a component and a browser global
 * (`matchMedia`, `localStorage`, `window` keydown) — the whole point is that no
 * app re-implements the SSR guard, the storage try/catch, or the listener
 * cleanup at each call site.
 *
 * Deliberately plain React, not Effect Atom: an Atom is for state shared across
 * the tree (the ownership table row that `themeAtom` occupies), while each of
 * these is per-component and subscribes to something the platform already owns.
 * Nothing here animates, so there is nothing to freeze for reduced motion.
 *
 * Each hook is a one-line wrapper over an imperative core exported below
 * (`mediaQuerySubscribe`/`mediaQuerySnapshot`, `readPersisted`/`writePersisted`,
 * `bindHotkey`, `debounceCore`). The split is what makes the browser behaviour
 * testable in this package, which depends on `react` but not on `react-dom` and
 * so has no renderer; the cores are the tested surface and are not part of the
 * barrel.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react"

// ---------------------------------------------------------------------------
// useMediaQuery
// ---------------------------------------------------------------------------

/**
 * Subscribe `onChange` to a media query, returning the unsubscribe. No-ops
 * (returning a no-op teardown) where `matchMedia` is absent — SSR, and jsdom.
 */
export const mediaQuerySubscribe = (query: string, onChange: () => void): (() => void) => {
  if (typeof window === "undefined" || !window.matchMedia) return () => {}
  const list = window.matchMedia(query)
  list.addEventListener("change", onChange)
  return () => list.removeEventListener("change", onChange)
}

/** Read whether a media query matches right now; `false` without `matchMedia`. */
export const mediaQuerySnapshot = (query: string): boolean => {
  if (typeof window === "undefined" || !window.matchMedia) return false
  return window.matchMedia(query).matches
}

/**
 * Whether a CSS media query currently matches, kept live.
 *
 * Uses `useSyncExternalStore` so the read is tear-free: the snapshot comes off
 * the live `MediaQueryList` on every render and the subscription re-renders on
 * `change`. The server snapshot is `false`, so a guarded surface renders its
 * full/wide path on the server and hydrates without claiming a match it could
 * not have measured.
 *
 * The query is a call-site argument rather than a shared preference store, so
 * each query owns its own subscription:
 * `useMediaQuery("(max-width: 34rem)")`, `useMediaQuery("(pointer: coarse)")`.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => mediaQuerySubscribe(query, onStoreChange),
    [query]
  )
  const getSnapshot = useCallback(() => mediaQuerySnapshot(query), [query])
  const getServerSnapshot = useMemo(() => () => false, [])
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

// ---------------------------------------------------------------------------
// usePersistentState
// ---------------------------------------------------------------------------

/**
 * Read and JSON-parse a persisted value, falling back on anything going wrong:
 * absent `window`, absent key, storage disabled, or malformed JSON. A stale or
 * corrupt entry must never be able to break a boot.
 */
export const readPersisted = <T>(key: string | undefined, fallback: T): T => {
  if (key === undefined || typeof window === "undefined") return fallback
  try {
    const raw = window.localStorage.getItem(key)
    return raw === null ? fallback : (JSON.parse(raw) as T)
  } catch {
    return fallback
  }
}

/**
 * Mirror a value into storage, best-effort. Private mode, a disabled store and
 * a quota overrun all throw here; the in-memory value stays authoritative and
 * persistence is silently dropped rather than surfacing as a crash.
 */
export const writePersisted = <T>(key: string | undefined, value: T): void => {
  if (key === undefined || typeof window === "undefined") return
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Storage unavailable — keep the in-memory value, drop persistence.
  }
}

/**
 * State that mirrors itself into `localStorage`, so a value survives a reload:
 * a collapsed rail, a pane split, a chosen tab.
 *
 * When `key` is `undefined` the hook degrades to plain `useState(fallback)` with
 * no persistence at all, which lets one instance opt out of storage without the
 * caller branching on the hook itself. The initial read is lazy (inside the
 * `useState` initializer) so it costs one read on mount, not one per render.
 */
export function usePersistentState<T>(
  key: string | undefined,
  fallback: T
): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(() => readPersisted(key, fallback))

  const set = useCallback(
    (value: T | ((prev: T) => T)) => {
      setState((prev) => {
        const next = value instanceof Function ? value(prev) : value
        writePersisted(key, next)
        return next
      })
    },
    [key]
  )

  return [state, set]
}

// ---------------------------------------------------------------------------
// useHotkey
// ---------------------------------------------------------------------------

/**
 * Whether "mod" should mean Cmd. Read from the platform at bind time (not at
 * module load) so a test can install a navigator before binding.
 */
const isMac = (): boolean => {
  if (typeof navigator === "undefined") return false
  return /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent || "")
}

/**
 * Parse a `"+"`-joined combo into its modifier flags and final key, lowercased.
 * "mod" is the platform-primary modifier, so one combo string covers both
 * Cmd-on-macOS and Ctrl-elsewhere.
 */
const parseCombo = (combo: string) => {
  const parts = combo
    .toLowerCase()
    .split("+")
    .map((part) => part.trim())
    .filter(Boolean)
  return {
    key: parts[parts.length - 1] ?? "",
    wantMod: parts.includes("mod"),
    wantShift: parts.includes("shift"),
    wantAlt: parts.includes("alt")
  }
}

/**
 * Attach a keydown listener on `window` for `combo` and return the teardown.
 * No-ops (no-op teardown) without a `window`, so an SSR render is safe.
 *
 * Modifier matching is EXACT in both directions: "mod+k" fires on Cmd/Ctrl+K
 * and NOT on Cmd+Shift+K, because a shortcut that also swallows its own
 * supersets would steal keystrokes from whatever owns the longer combo.
 * `preventDefault` runs only on a match — the browser's own `mod+k` would
 * otherwise focus the URL bar.
 */
export const bindHotkey = (combo: string, invoke: () => void): (() => void) => {
  if (typeof window === "undefined") return () => {}
  const { key, wantMod, wantShift, wantAlt } = parseCombo(combo)
  const mac = isMac()

  const onKeyDown = (event: KeyboardEvent): void => {
    const primary = mac ? event.metaKey : event.ctrlKey
    if (wantMod !== primary) return
    if (wantShift !== event.shiftKey) return
    if (wantAlt !== event.altKey) return
    if (event.key.toLowerCase() !== key) return
    event.preventDefault()
    invoke()
  }

  window.addEventListener("keydown", onKeyDown)
  return () => window.removeEventListener("keydown", onKeyDown)
}

/**
 * Bind a keyboard shortcut for as long as the component is mounted. `combo` is
 * a `"+"`-joined string of modifiers plus one key — `"mod+k"`, `"shift+/"` —
 * where "mod" maps to Cmd on macOS and Ctrl elsewhere.
 *
 * The latest `handler` is held in a ref and the effect depends only on `combo`,
 * so passing a fresh inline closure every render does NOT re-bind the listener
 * (the common shape: `useHotkey("mod+k", () => setOpen((o) => !o))`).
 */
export function useHotkey(combo: string, handler: () => void): void {
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => bindHotkey(combo, () => handlerRef.current()), [combo])
}

// ---------------------------------------------------------------------------
// useDebounced
// ---------------------------------------------------------------------------

/**
 * Schedule `emit(value)` for `ms` from now and return the cancel. Trailing edge
 * only: nothing is emitted up front, so a caller that cancels before the delay
 * elapses emits nothing at all. That cancel-then-reschedule pair is what turns a
 * per-keystroke stream into one settled value.
 */
export const debounceCore = <T>(value: T, ms: number, emit: (value: T) => void): (() => void) => {
  const timer = setTimeout(() => emit(value), ms)
  return () => clearTimeout(timer)
}

/**
 * A value that lags behind its source, settling only once the source has been
 * still for `ms`. The classic use is a typeahead: the user types six characters
 * and one search runs.
 *
 * DEBOUNCE THE KEY, NEVER THE QUERY FUNCTION. Feed this value into `queryKey`
 * and let Query do the rest — it cancels the superseded request itself (the
 * `signal` reaches the Effect fiber through `effectQueryFn`) and caches one
 * entry per settled term, so going back to an earlier term is free. A debounce
 * placed inside `queryFn` instead defeats both: the key never changes, so there
 * is nothing for Query to cancel and nothing to cache per term.
 *
 * ```ts
 * const [term, setTerm] = useState("")
 * const debounced = useDebounced(term, 250)
 * const results = useQuery({
 *   queryKey: ["projects", "search", debounced],
 *   queryFn: effectQueryFn(ProjectService.search(debounced)),
 *   enabled: debounced.trim().length >= 2,   // no request for one stray letter
 *   placeholderData: keepPreviousData        // no flash to empty between terms
 * })
 * ```
 *
 * `enabled` on a minimum length keeps a single keystroke from firing a request
 * the user did not mean, and `keepPreviousData` holds the previous result list
 * on screen while the next one loads rather than blanking it.
 *
 * The first render returns the value as-is (there is nothing to wait for yet);
 * every later change waits out the delay. Changing `ms` restarts the pending
 * wait, which is the honest reading of a new delay.
 */
export function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState<T>(value)

  useEffect(() => debounceCore(value, ms, setDebounced), [value, ms])

  return debounced
}
